import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { FinancialObservationPanel } from "@/components/member-financials/financial-observations";
import { MEMBER_STATUS_OPTIONS, bucketMemberStatus } from "@shared/member-status";
import { residentEvidenceLabel, type MemberResidencyView } from "@shared/broadcast-recipient-review";
import { memberLifecycle, formatMemberTenure } from "@shared/member-lifecycle";
import { formatDateOnly } from "@/lib/format-date-only";
import { usePageTitle } from "@/hooks/use-page-title";
import { MemberBookingRequestSection } from "@/components/member-booking-request-section";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { useRoute, useLocation, Link } from "wouter";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useMemberPortalOptIn } from "@/components/member-portal-beta";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import AppLayout from "@/components/app-layout";
import { MemberSourceBadge } from "@/components/member-source-badge";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { NewMessageModal } from "@/components/new-message-modal";
import { MessageAttachments } from "@/components/message-attachments";
import { MessageBody } from "@/pages/message-body-render";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Member, MemberChat, PadsplitStayReview, PadsplitMoveInReview } from "@shared/schema";
import { getMemberDisplayName, getMemberRating } from "@shared/member-display";
import { getPadsplitProfileUrl } from "@shared/padsplit-profile-url";

// Task #1087 — /api/app/members/:id/timeline now joins member_chat_attachments
// onto each row. Inbound MMS without a sidecar entry (older harvested rows)
// fall back to URLs extracted from the message body — narrowed to URLs that
// look like media so ordinary inbound links don't render as spurious chips.
type TimelineChat = MemberChat & { attachments?: string[] };

const TIMELINE_URL_REGEX = /https?:\/\/[^\s)]+/gi;
const TIMELINE_MEDIA_EXT_REGEX = /\.(?:jpe?g|png|gif|webp|heic|heif|pdf)(?:[?#]|$)/i;
const TIMELINE_TWILIO_MEDIA_REGEX = /\.twilio\.com\/.+\/Media\//i;

function timelineAttachments(chat: TimelineChat): string[] {
  if (chat.attachments && chat.attachments.length > 0) return chat.attachments;
  if (chat.senderType === "admin" || !chat.messageText) return [];
  const matches = chat.messageText.match(TIMELINE_URL_REGEX);
  if (!matches) return [];
  return matches
    .map((m) => m.replace(/[.,;]+$/, ""))
    .filter((u) => TIMELINE_MEDIA_EXT_REGEX.test(u) || TIMELINE_TWILIO_MEDIA_REGEX.test(u));
}

type MemberWithRoom = Member & { roomNumber?: string | null; currentPropertyId?: number | null; residency?: MemberResidencyView | null };
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, User, Mail, Phone, Home, Calendar, Car, Dog, AlertTriangle,
  DollarSign, Pencil, Save, X, MessageSquare, ExternalLink, Star,
  Sparkles, Clock, ChevronRight, Copy, Hash, LogOut, History, UserPlus
} from "lucide-react";

type TenancyHistoryEntry = {
  id: number;
  propertyId: number | null;
  propertyName: string | null;
  propertyAddress: string | null;
  archived: boolean;
};

export default function AppMemberDetail() {
  return <AppLayout><MemberDetailContent /></AppLayout>;
}

// All permission-dependent hooks must execute inside AppLayout's provider.
function MemberDetailContent() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("members");
  const [, params] = useRoute("/app/members/:id");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const memberId = params?.id;

  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});

  const memberQuery = useQuery<MemberWithRoom>({
    queryKey: ["/api/app/devices/members", memberId],
    queryFn: async () => {
      const res = await fetch(`/api/app/devices/members/${memberId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!memberId,
  });
  usePageTitle(memberQuery.data?.name || "Member");

  // Task #2000 — tenancy history for this member (active + archived rows).
  const tenancyHistoryQuery = useQuery<TenancyHistoryEntry[]>({
    queryKey: ["/api/app/devices/members", memberId, "tenancy-history"],
    queryFn: async () => {
      const res = await fetch(`/api/app/devices/members/${memberId}/tenancy-history`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!memberId,
  });

  const propertiesQuery = useQuery<{ id: number; name: string | null; address: string | null; status: string }[]>({
    queryKey: ["/api/app/properties"],
  });

  const editPropertyId = editData.propertyId && editData.propertyId !== "none" ? editData.propertyId : null;
  const roomsForEditQuery = useQuery<{ id: number; roomNumber: string; shadowOfRoomId?: number | null }[]>({
    queryKey: ["/api/app/properties", editPropertyId, "rooms", "bedroomOnly"],
    queryFn: async () => {
      // Task #4157 — bedroomOnly so the member room picker only ever offers real
      // bedrooms (never bathrooms or shadow rows). A member must never be linked
      // to a non-bedroom; the write routes reject it server-side too.
      const res = await fetch(`/api/app/properties/${editPropertyId}/rooms?bedroomOnly=true`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!editPropertyId,
  });

  // Task #1447 — property assignment goes through a dedicated endpoint.
  // `saveEditing` packages a `__propertyChange` envelope when needed;
  // direct mutate({...patchFields}) calls (no envelope) keep working.
  type PropertyChange = { propertyId: number | null; roomId: number | null };
  type UpdateMemberInput =
    | { __propertyChange: PropertyChange | null; patch: Record<string, unknown> }
    | Record<string, unknown>;
  function isEnvelope(
    input: UpdateMemberInput,
  ): input is { __propertyChange: PropertyChange | null; patch: Record<string, unknown> } {
    return "patch" in input && "__propertyChange" in input;
  }
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateMemberInput) => {
      const propertyChange = isEnvelope(data) ? data.__propertyChange : null;
      const patch = isEnvelope(data) ? data.patch : data;
      if (propertyChange) {
        await apiRequest("PUT", `/api/app/devices/members/${memberId}/property`, propertyChange);
      }
      return apiRequest("PATCH", `/api/app/devices/members/${memberId}`, patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/members", memberId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/members"] });
      setEditing(false);
      toast({ title: "Member updated" });
    },
    onError: () => {
      toast({ title: "Failed to update member", variant: "destructive" });
    },
  });

  // Member portal — invite this (direct-only) member. The server rejects
  // non-direct members and emails/texts a welcome with the login link.
  const invitePortalMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/app/members/${memberId}/invite-portal`),
    onSuccess: () => {
      toast({ title: "Portal invite sent", description: "They'll get a welcome with their login link." });
    },
    onError: (err) => {
      toast({
        title: "Could not send portal invite",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const member = memberQuery.data;
  // B14 — the account-level member portal opt-in gates "Invite to Portal".
  const portalOptInQuery = useMemberPortalOptIn({ enabled: !!member && (member.type ?? "direct") === "direct" });
  const portalOptedIn = portalOptInQuery.data?.optedIn === true;
  // Real `rating` only (no average_rating fallback) — see getMemberRating.
  const memberRating = getMemberRating(member);
  const allProperties = propertiesQuery.data || [];
  const properties = allProperties.filter((p) => {
    if (p.status === "active") return true;
    if (member?.currentPropertyId && p.id === member.currentPropertyId) return true;
    return false;
  });

  function startEditing() {
    if (!member) return;
    setEditData({
      name: member.name || "",
      displayName: member.displayName || "",
      legalName: member.legalName || "",
      email: member.email || "",
      phone: member.phone || "",
      propertyId: member.currentPropertyId ? String(member.currentPropertyId) : "",
      roomId: member.roomId ? String(member.roomId) : "",
      // Normalize raw PadSplit spellings ("moved-out", "booking request", …)
      // onto the canonical dropdown values; unknown raw values pass through
      // untouched so nothing is silently rewritten.
      status: (() => {
        const bucket = bucketMemberStatus(member.status);
        return bucket !== "unknown" ? bucket : (member.status || "active");
      })(),
      type: ((member.type ?? "direct") as "direct" | "padsplit"),
      padsplitMemberId: member.padsplitMemberId || "",
      carMake: member.carMake || "",
      carModel: member.carModel || "",
      carYear: member.carYear || "",
      carColor: member.carColor || "",
      licensePlate: member.licensePlate || "",
      licensePlateState: member.licensePlateState || "",
      emergencyContactName: member.emergencyContactName || "",
      emergencyContactPhone: member.emergencyContactPhone || "",
      emergencyContactRelationship: member.emergencyContactRelationship || "",
      hasServiceAnimal: member.hasServiceAnimal || false,
      serviceAnimalType: member.serviceAnimalType || "",
      paymentStatus: member.paymentStatus || "current",
      weeklyRate: member.weeklyRate || "",
      totalDue: member.totalDue || "",
      paymentFrequency: member.paymentFrequency || "weekly",
      moveInDate: member.moveInDate ? new Date(member.moveInDate).toISOString().split("T")[0] : "",
    });
    setEditing(true);
  }

  function saveEditing() {
    const { propertyId: rawPropertyId, paymentStatus: _ignoredPaymentStatus, ...rest } = editData;
    // Canonical dropdown defaults must not echo a harvested request as a
    // deliberate lifecycle transition on unrelated contact/name edits.
    const originalBucket = bucketMemberStatus(member?.status);
    const originalStatus = originalBucket !== "unknown" ? originalBucket : (member?.status || "active");
    if (rest.status === originalStatus) delete rest.status;
    const payload: Record<string, any> = { ...rest };
    const nextPropertyId =
      rawPropertyId && rawPropertyId !== "none"
        ? (() => {
            const parsed = parseInt(rawPropertyId, 10);
            return isNaN(parsed) ? null : parsed;
          })()
        : null;
    if (payload.roomId) {
      const parsed = parseInt(payload.roomId, 10);
      payload.roomId = isNaN(parsed) ? null : parsed;
    } else {
      payload.roomId = null;
    }
    if (payload.weeklyRate === "") payload.weeklyRate = null;
    if (payload.totalDue === "") payload.totalDue = null;
    if (payload.moveInDate === "") payload.moveInDate = null;
    // `name` is NOT NULL in the DB — a cleared input means "keep the current
    // name", not "blank it". The nullable display/legal names clear to NULL.
    if (payload.name === "") delete payload.name;
    if (payload.displayName === "") payload.displayName = null;
    if (payload.legalName === "") payload.legalName = null;
    if (member?.padsplitMemberId) delete payload.padsplitMemberId;
    if (payload.padsplitMemberId === "") delete payload.padsplitMemberId;
    // Task #1447 — property assignment goes through its own endpoint.
    // Push it first so the room/property consistency check on the PATCH
    // route sees the new property when room is also changing.
    updateMutation.mutate({
      __propertyChange:
        nextPropertyId !== (member?.currentPropertyId ?? null)
          ? { propertyId: nextPropertyId, roomId: payload.roomId ?? null }
          : null,
      patch: payload,
    });
  }

  function getPropertyName(propId: number | null | undefined) {
    if (!propId) return "Unassigned";
    const prop = allProperties.find(p => p.id === propId);
    return prop?.name || prop?.address || `Property #${propId}`;
  }

  if (memberQuery.isLoading) {
    return (
      <>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </>
    );
  }

  if (!member) {
    return (
      <>
        <div className="p-6 text-center">
          <p className="text-gray-500">Member not found.</p>
          <Button variant="outline" className="mt-4" asChild>
            <Link href="/app/members">Back to Members</Link>
          </Button>
        </div>
      </>
    );
  }

  const profileUrl = getPadsplitProfileUrl({ occupantId: member.occupantId });
  const lifecycle = memberLifecycle(member);
  const statusColor =
    lifecycle.bucket === "active"
      ? "bg-green-100 text-green-700"
      : lifecycle.bucket === "terminated"
        ? "bg-red-100 text-red-700"
        : "bg-gray-100 text-gray-600";

  return (
    <>
      <div className="p-6 max-w-5xl">
        <ViewOnlyBanner area="members" />
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" asChild data-testid="button-back-members">
            <Link href="/app/members">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Members
            </Link>
          </Button>
        </div>

        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-brand-muted flex items-center justify-center">
              <User className="h-8 w-8 text-brand" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="text-member-detail-name" data-tour="member-detail-heading">{getMemberDisplayName(member)}</h1>
              {member.legalName && String(member.legalName).trim() && member.legalName !== getMemberDisplayName(member) && (
                <p className="text-xs text-gray-500 mt-0.5" data-testid="text-member-detail-legal-name">
                  Legal: {member.legalName}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500">Stored lifecycle:</span>
                <Badge className={lifecycle.archivedApplicant ? "bg-gray-100 text-gray-600" : statusColor} data-testid="text-member-detail-status">{lifecycle.label}</Badge>
                <span className="text-xs text-gray-500" data-testid="text-member-detail-residency">{residentEvidenceLabel(member.residency)}</span>
                {lifecycle.outcome && <span className="text-xs capitalize text-gray-500">{lifecycle.outcome}</span>}
                <MemberSourceBadge
                  padsplitMemberId={
                    (member.type ?? "direct") === "padsplit"
                      ? member.padsplitMemberId ?? "padsplit"
                      : null
                  }
                  memberId={member.id}
                  size="md"
                  href={profileUrl}
                />
                {profileUrl ? (
                  <a
                    href={profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                    data-testid="link-view-in-padsplit"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View in PadSplit
                  </a>
                ) : (member.type ?? "direct") === "padsplit" ? (
                  <span className="text-xs text-gray-500" data-testid="text-padsplit-profile-unavailable">PadSplit profile unavailable</span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (member.type ?? "direct") === "direct" && (
              // B14 — inviting to the member portal needs the account opt-in
              // (Members page → "Member portal").
              <div className="flex flex-col items-end gap-0.5">
                <Button
                  disabled={readOnly || invitePortalMutation.isPending || !portalOptedIn}
                  variant="outline"
                  size="sm"
                  onClick={() => invitePortalMutation.mutate()}
                  title={portalOptedIn ? "Invite this member to your member portal" : "Turn on the member portal on the Members page first."}
                  data-testid="button-invite-member-portal"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  {invitePortalMutation.isPending ? "Inviting…" : "Invite to Portal"}
                </Button>
                {portalOptInQuery.isSuccess && !portalOptedIn && (
                  <Link href="/app/members" className="text-[11px] text-brand hover:underline" data-testid="link-member-portal-opt-in">
                    Turn on the member portal
                  </Link>
                )}
              </div>
            )}
            {!editing && (
              <Button disabled={readOnly} variant="outline" size="sm" onClick={startEditing} data-testid="button-edit-member" data-tour="member-detail-profile">
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
            {!editing && lifecycle.archivedApplicant && (
              <Button disabled={readOnly || updateMutation.isPending} variant="outline" size="sm"
                onClick={() => updateMutation.mutate({ status: "booking_request", reopenApplication: true })}
                data-testid="button-reopen-application">
                {updateMutation.isPending ? "Reopening…" : "Reopen application"}
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditing(false)} data-testid="button-cancel-edit">
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={saveEditing} disabled={readOnly || updateMutation.isPending} data-testid="button-save-member">
                  <Save className="h-4 w-4 mr-1" />
                  {updateMutation.isPending ? "Saving..." : "Save"}
                </Button>
              </>
            )}
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="member-detail" />
              <GuideButton pageKey="member-detail" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="member-detail" />
        </div>

        {member.mergedIntoMemberId != null && (
          <div
            className="mb-6 flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-4"
            data-testid="banner-member-transferred"
          >
            <History className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-blue-900">This member transferred</p>
              <p className="text-blue-800 mt-0.5">
                This stay was merged into the member&apos;s current stay — the
                conversation continues there.
              </p>
              <Button variant="outline" size="sm" className="mt-2" asChild data-testid="link-transferred-canonical-member">
                <Link href={`/app/members/${member.mergedIntoMemberId}`}>
                  View current stay
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        )}

        {!lifecycle.archivedApplicant && !lifecycle.needsReview && (member.status === "moved-out" || member.status === "terminated") && (
          <div
            className="mb-6 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4"
            data-testid="banner-member-moved-out"
          >
            <LogOut className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900">
                Moved out
                {lifecycle.actualMoveOutDate
                  ? ` on ${lifecycle.actualMoveOutDate.toLocaleDateString()}`
                  : ""}
              </p>
              <p className="text-amber-800 mt-0.5">
                This member is no longer assigned to a property. Their tenancy
                history is preserved below.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Section title="Contact Information" icon={<Mail className="h-4 w-4" />}>
            {editing ? (
              <div className="space-y-3">
                <Field label="Name">
                  <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} data-testid="input-edit-name" />
                </Field>
                <Field label="Display Name">
                  <Input value={editData.displayName} onChange={(e) => setEditData({ ...editData, displayName: e.target.value })} placeholder="Preferred name shown in the app" data-testid="input-edit-display-name" />
                </Field>
                <Field label="Legal Name">
                  <Input value={editData.legalName} onChange={(e) => setEditData({ ...editData, legalName: e.target.value })} data-testid="input-edit-legal-name" />
                </Field>
                <Field label="Email">
                  <Input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} data-testid="input-edit-email" />
                </Field>
                <Field label="Phone">
                  <Input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} data-testid="input-edit-phone" />
                </Field>
                <Field label="PadSplit UID">
                  {member.padsplitMemberId ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Input
                            value={member.padsplitMemberId}
                            readOnly
                            tabIndex={-1}
                            className="bg-muted text-muted-foreground cursor-not-allowed"
                            data-testid="input-edit-padsplit-id"
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          PadSplit UID is locked once set. Contact support if it needs to change.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Input value={editData.padsplitMemberId} onChange={(e) => setEditData({ ...editData, padsplitMemberId: e.target.value })} placeholder="e.g. 158106" data-testid="input-edit-padsplit-id" />
                  )}
                </Field>
                {member.occupantId && (
                  <Field label="Occupant ID">
                    <div className="flex gap-2">
                      <Input
                        value={member.occupantId}
                        readOnly
                        tabIndex={-1}
                        className="bg-muted text-muted-foreground cursor-not-allowed"
                        data-testid="input-edit-occupant-id"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          navigator.clipboard.writeText(member.occupantId!);
                          toast({ title: "Copied", description: "Occupant ID copied to clipboard" });
                        }}
                        data-testid="button-copy-occupant-id"
                        aria-label="Copy occupant ID"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </Field>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {/* These three rows mirror the three editable fields above. The
                    "Name" row must render `name` itself — rendering
                    getMemberDisplayName() here made a host's edit to Name
                    invisible whenever displayName was set (the helper prefers
                    displayName), which read as "the edit didn't save". */}
                <InfoRow icon={<User className="h-4 w-4 text-gray-400" />} label="Name" value={member.name || "—"} testId="text-detail-name" />
                {member.displayName && String(member.displayName).trim() && member.displayName !== member.name && (
                  <InfoRow
                    icon={<User className="h-4 w-4 text-gray-400" />}
                    label="Display name"
                    value={member.displayName}
                    testId="text-detail-display-name"
                  />
                )}
                {member.legalName && String(member.legalName).trim() && (
                  <InfoRow
                    icon={<User className="h-4 w-4 text-gray-400" />}
                    label="Legal name"
                    value={member.legalName}
                    testId="text-detail-legal-name"
                  />
                )}
                <InfoRow icon={<Mail className="h-4 w-4 text-gray-400" />} label="Email" value={member.email || "—"} testId="text-detail-email" />
                <InfoRow icon={<Phone className="h-4 w-4 text-gray-400" />} label="Phone" value={member.phone || "—"} testId="text-detail-phone" />
                {member.padsplitMemberId && <InfoRow icon={<ExternalLink className="h-4 w-4 text-gray-400" />} label="PadSplit UID" value={member.padsplitMemberId} testId="text-detail-padsplit-id" />}
                {member.occupantId && (
                  <div className="flex items-center gap-2" data-testid="row-detail-occupant-id">
                    <Hash className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-500 min-w-[110px]">Occupant ID</span>
                    <span className="text-sm font-mono" data-testid="text-detail-occupant-id">{member.occupantId}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => {
                        navigator.clipboard.writeText(member.occupantId!);
                        toast({ title: "Copied", description: "Occupant ID copied to clipboard" });
                      }}
                      data-testid="button-copy-occupant-id-view"
                      aria-label="Copy occupant ID"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Section>

          <Section title="Housing" icon={<Home className="h-4 w-4" />}>
            {editing ? (
              <div className="space-y-3">
                <Field label="Property">
                  <Select value={editData.propertyId} onValueChange={(v) => setEditData({ ...editData, propertyId: v, roomId: "" })}>
                    <SelectTrigger data-testid="select-edit-property">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.name || p.address || `Property #${p.id}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Room">
                  <Select
                    value={editData.roomId || "none"}
                    onValueChange={(v) => setEditData({ ...editData, roomId: v === "none" ? "" : v })}
                    disabled={!editPropertyId}
                  >
                    <SelectTrigger data-testid="select-edit-room">
                      <SelectValue placeholder={editPropertyId ? "Select a room" : "Select a property first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {(roomsForEditQuery.data || []).map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>{r.roomNumber}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                    <SelectTrigger data-testid="select-edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MEMBER_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Type">
                  <Select
                    value={editData.type}
                    onValueChange={(v) =>
                      setEditData({ ...editData, type: v as "direct" | "padsplit" })
                    }
                    disabled={!!member.padsplitMemberId}
                  >
                    <SelectTrigger data-testid="select-edit-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct</SelectItem>
                      <SelectItem value="padsplit">PadSplit</SelectItem>
                    </SelectContent>
                  </Select>
                  {member.padsplitMemberId && (
                    <p className="text-xs text-gray-500 mt-1">
                      Locked to PadSplit because this member is synced from PadSplit.
                    </p>
                  )}
                </Field>
                <Field label={lifecycle.plannedMoveInDate || lifecycle.archivedApplicant ? "Planned move-in (not residency)" : "Move-in Date"}>
                  <Input type="date" value={editData.moveInDate} onChange={(e) => setEditData({ ...editData, moveInDate: e.target.value })} data-testid="input-edit-movein" />
                </Field>
              </div>
            ) : (
              <div className="space-y-2">
                <InfoRow icon={<Home className="h-4 w-4 text-gray-400" />} label="Property" value={getPropertyName(member.currentPropertyId ?? null)} testId="text-detail-property" />
                {member.roomId && <InfoRow icon={<Home className="h-4 w-4 text-gray-400" />} label="Room" value={member.roomNumber ? (/^room\s/i.test(member.roomNumber) ? member.roomNumber : `Room ${member.roomNumber}`) : "Room (unnamed)"} testId="text-detail-room" />}
                <InfoRow icon={<Calendar className="h-4 w-4 text-gray-400" />} label="Move-in Date" value={formatDateOnly(lifecycle.actualMoveInDate) ?? "—"} testId="text-detail-movein" />
                <InfoRow icon={<Calendar className="h-4 w-4 text-gray-400" />} label="Move-out Date" value={formatDateOnly(lifecycle.actualMoveOutDate) ?? "—"} testId="text-detail-moveout" />
                <InfoRow icon={<Clock className="h-4 w-4 text-gray-400" />} label="Tenure" value={formatMemberTenure(lifecycle.tenureDays)} testId="text-detail-tenure" />
                {lifecycle.plannedMoveInDate && <InfoRow icon={<Calendar className="h-4 w-4 text-gray-400" />} label="Planned move-in (not residency)" value={formatDateOnly(lifecycle.plannedMoveInDate) ?? "—"} testId="text-detail-planned-movein" />}
              </div>
            )}
          </Section>

          <MemberBookingRequestSection memberId={member.id} />

          <Section title="Vehicle Information" icon={<Car className="h-4 w-4" />}>
            {editing ? (
              <div className="space-y-3">
                <Field label="Make">
                  <Input value={editData.carMake} onChange={(e) => setEditData({ ...editData, carMake: e.target.value })} placeholder="e.g. Toyota" data-testid="input-edit-car-make" />
                </Field>
                <Field label="Model">
                  <Input value={editData.carModel} onChange={(e) => setEditData({ ...editData, carModel: e.target.value })} placeholder="e.g. Camry" data-testid="input-edit-car-model" />
                </Field>
                <Field label="Year">
                  <Input value={editData.carYear} onChange={(e) => setEditData({ ...editData, carYear: e.target.value })} placeholder="e.g. 2021" data-testid="input-edit-car-year" />
                </Field>
                <Field label="Color">
                  <Input value={editData.carColor} onChange={(e) => setEditData({ ...editData, carColor: e.target.value })} placeholder="e.g. Silver" data-testid="input-edit-car-color" />
                </Field>
                <Field label="License Plate">
                  <Input value={editData.licensePlate} onChange={(e) => setEditData({ ...editData, licensePlate: e.target.value })} data-testid="input-edit-license" />
                </Field>
                <Field label="Plate State">
                  <Input value={editData.licensePlateState} onChange={(e) => setEditData({ ...editData, licensePlateState: e.target.value })} placeholder="e.g. TX" data-testid="input-edit-license-state" />
                </Field>
              </div>
            ) : (
              <div className="space-y-2">
                {member.carMake || member.carModel ? (
                  <>
                    <InfoRow icon={<Car className="h-4 w-4 text-gray-400" />} label="Vehicle" value={[member.carColor, member.carYear, member.carMake, member.carModel].filter(Boolean).join(" ") || "—"} testId="text-detail-car" />
                    {member.licensePlate && <InfoRow icon={<Car className="h-4 w-4 text-gray-400" />} label="License Plate" value={[member.licensePlateState, member.licensePlate].filter(Boolean).join(" ")} testId="text-detail-license" />}
                  </>
                ) : (
                  <p className="text-sm text-gray-400 italic">No vehicle information on file.</p>
                )}
              </div>
            )}
          </Section>

          <Section title="Emergency Contact" icon={<AlertTriangle className="h-4 w-4" />}>
            {editing ? (
              <div className="space-y-3">
                <Field label="Name">
                  <Input value={editData.emergencyContactName} onChange={(e) => setEditData({ ...editData, emergencyContactName: e.target.value })} data-testid="input-edit-ec-name" />
                </Field>
                <Field label="Phone">
                  <Input value={editData.emergencyContactPhone} onChange={(e) => setEditData({ ...editData, emergencyContactPhone: e.target.value })} data-testid="input-edit-ec-phone" />
                </Field>
                <Field label="Relationship">
                  <Input value={editData.emergencyContactRelationship} onChange={(e) => setEditData({ ...editData, emergencyContactRelationship: e.target.value })} placeholder="e.g. Mother, Spouse" data-testid="input-edit-ec-relationship" />
                </Field>
              </div>
            ) : (
              <div className="space-y-2">
                {member.emergencyContactName ? (
                  <>
                    <InfoRow icon={<User className="h-4 w-4 text-gray-400" />} label="Name" value={member.emergencyContactName} testId="text-detail-ec-name" />
                    <InfoRow icon={<Phone className="h-4 w-4 text-gray-400" />} label="Phone" value={member.emergencyContactPhone || "—"} testId="text-detail-ec-phone" />
                    {member.emergencyContactRelationship && <InfoRow icon={<User className="h-4 w-4 text-gray-400" />} label="Relationship" value={member.emergencyContactRelationship} testId="text-detail-ec-rel" />}
                  </>
                ) : (
                  <p className="text-sm text-gray-400 italic">No emergency contact on file.</p>
                )}
              </div>
            )}
          </Section>

          <Section title="Service Animal" icon={<Dog className="h-4 w-4" />}>
            {editing ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch checked={editData.hasServiceAnimal} onCheckedChange={(v) => setEditData({ ...editData, hasServiceAnimal: v })} data-testid="switch-edit-service-animal" />
                  <Label className="text-sm">Has service animal</Label>
                </div>
                {editData.hasServiceAnimal && (
                  <Field label="Animal Type">
                    <Input value={editData.serviceAnimalType} onChange={(e) => setEditData({ ...editData, serviceAnimalType: e.target.value })} placeholder="e.g. Dog, Cat" data-testid="input-edit-animal-type" />
                  </Field>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <InfoRow icon={<Dog className="h-4 w-4 text-gray-400" />} label="Service Animal" value={member.hasServiceAnimal ? "Yes" : "No"} testId="text-detail-service-animal" />
                {member.hasServiceAnimal && member.serviceAnimalType && (
                  <InfoRow icon={<Dog className="h-4 w-4 text-gray-400" />} label="Type" value={member.serviceAnimalType} testId="text-detail-animal-type" />
                )}
              </div>
            )}
          </Section>

          <Section title="Stored financials (unverified)" icon={<DollarSign className="h-4 w-4" />}>
            <p className="text-xs text-gray-500 mb-3">Legacy stored values, not approved source observations. Their freshness and balance sign semantics are unverified.</p>
            {editing ? (
              <div className="space-y-3">
                <Field label="Weekly Rate ($, stored)">
                  <Input type="number" value={editData.weeklyRate} onChange={(e) => setEditData({ ...editData, weeklyRate: e.target.value })} data-testid="input-edit-weekly-rate" />
                </Field>
                <Field label="Total Due ($, stored / unverified)">
                  <Input type="number" value={editData.totalDue} onChange={(e) => setEditData({ ...editData, totalDue: e.target.value })} data-testid="input-edit-total-due" />
                </Field>
                <Field label="Payment Frequency">
                  <Select value={editData.paymentFrequency} onValueChange={(v) => setEditData({ ...editData, paymentFrequency: v })}>
                    <SelectTrigger data-testid="select-edit-payment-freq">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            ) : (
              <div className="space-y-2">
                {member.paymentStatus && <InfoRow icon={<DollarSign className="h-4 w-4 text-gray-400" />} label="Stored status" value={member.paymentStatus} testId="text-detail-payment-status" />}
                {member.weeklyRate && <InfoRow icon={<DollarSign className="h-4 w-4 text-gray-400" />} label="Weekly Rate (stored)" value={`$${Number(member.weeklyRate).toFixed(2)}`} testId="text-detail-weekly-rate" />}
                {member.totalDue != null && String(member.totalDue) !== "" && <InfoRow icon={<DollarSign className="h-4 w-4 text-gray-400" />} label="Total Due (stored)" value={Number(member.totalDue) < 0 ? `-$${Math.abs(Number(member.totalDue)).toFixed(2)}` : `$${Number(member.totalDue).toFixed(2)}`} testId="text-detail-total-due" />}
                {member.currentDues != null && String(member.currentDues) !== "" && <InfoRow icon={<DollarSign className="h-4 w-4 text-gray-400" />} label="Dues (stored)" value={String(member.currentDues)} testId="text-detail-current-dues-stored" />}
                <InfoRow icon={<Calendar className="h-4 w-4 text-gray-400" />} label="Frequency" value={member.paymentFrequency || "weekly"} testId="text-detail-frequency" />
                {member.lastPaymentDate && <InfoRow icon={<Calendar className="h-4 w-4 text-gray-400" />} label="Last Payment (stored)" value={new Date(member.lastPaymentDate).toLocaleDateString()} testId="text-detail-last-payment" />}
              </div>
            )}
          </Section>

          <Section title="Payments & Risk" icon={<Star className="h-4 w-4" />}>
            <div className="space-y-3">
              <div
                className="flex items-center gap-1.5 text-[11px] text-gray-500 -mt-1 mb-1"
                data-testid="text-harvest-managed-note"
              >
                <Sparkles className="h-3 w-3 text-blue-500" />
                <span>Stored PadSplit rating. Source observations below are separate evidence.</span>
              </div>
              {/* Rating is the one field in this section with a confirmed
                  upstream source: the harvest is being wired to write
                  members.rating from PadSplit's occupancy.memberScoreForOccupancy.
                  Renders the real score the moment one lands, and falls back to
                  the not-yet-available pill until then — no further deploy
                  needed to light it up. */}
              <HarvestRow
                icon={<Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />}
                label="Rating"
                testId="text-detail-rating"
                hasValue={true}
                placeholder=""
              >
                {memberRating !== null ? (
                  <span className="text-sm font-medium text-gray-900">{memberRating.toFixed(1)}</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">Not yet available</span>
                )}
              </HarvestRow>
              <FinancialObservationPanel memberId={member.id} propertyId={member.currentPropertyId} />
            </div>
          </Section>
        </div>

        <div className="mt-6">
          <Section title="Tenancy History" icon={<History className="h-4 w-4" />}>
            {tenancyHistoryQuery.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (tenancyHistoryQuery.data?.length ?? 0) === 0 ? (
              <p className="text-sm text-gray-400 italic" data-testid="text-tenancy-history-empty">
                No property assignments on record.
              </p>
            ) : (
              <ol className="space-y-3" data-testid="list-tenancy-history">
                {(tenancyHistoryQuery.data ?? []).map((row, idx) => {
                  const isMostRecent = idx === 0;
                  // Manually-toggled moved-out members may still have an
                  // unarchived row — treat the most recent row as ended
                  // whenever the member's status is "moved-out", so the UI
                  // never shows Active/Present for a moved-out member.
                  const memberMovedOut = !lifecycle.needsReview && member.status === "moved-out";
                  const showAsActive =
                    !row.archived && !(isMostRecent && memberMovedOut);
                  const startLabel = lifecycle.actualMoveInDate && isMostRecent
                    ? lifecycle.actualMoveInDate.toLocaleDateString()
                    : null;
                  const endLabel = showAsActive
                    ? "Present"
                    : isMostRecent && memberMovedOut && member.updatedAt
                      ? new Date(member.updatedAt).toLocaleDateString()
                      : null;
                  const propLabel =
                    row.propertyName ||
                    row.propertyAddress ||
                    (row.propertyId ? `Property #${row.propertyId}` : "Unassigned");
                  return (
                    <li
                      key={row.id}
                      className="flex items-start gap-3 rounded-md border border-gray-200 p-3"
                      data-testid={`row-tenancy-history-${row.id}`}
                    >
                      <Home className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {row.propertyId ? (
                            <Link
                              href={`/app/properties/${row.propertyId}`}
                              className="text-sm font-medium text-brand hover:underline truncate"
                              data-testid={`link-tenancy-property-${row.id}`}
                            >
                              {propLabel}
                            </Link>
                          ) : (
                            <span className="text-sm font-medium text-gray-700 truncate">
                              {propLabel}
                            </span>
                          )}
                          {showAsActive ? (
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">Past</Badge>
                          )}
                        </div>
                        {(startLabel || endLabel) && (
                          <p className="text-xs text-gray-500 mt-1" data-testid={`text-tenancy-dates-${row.id}`}>
                            {startLabel ?? "Start date unknown"} → {endLabel ?? "End date unknown"}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Section>
        </div>

        <MemberReviewsSection memberId={member.id} memberName={member.name ?? ""} />
        {/* ?exact=1 — the host is viewing THIS stay row; open its thread as-is
            rather than letting the messages page's active-stay deep-link
            resolution redirect a closed stay to the person's successor. */}
        <MemberMessagesPanel memberId={member.id} memberName={member.name} onOpenFull={() => navigate(`/app/messages?memberId=${member.id}&exact=1`)} />
      </div>
    </>
  );
}

// Task #1029 — Reads from member_chats via the new timeline endpoint and
// enqueues outbound replies through messages_pending.
function MemberMessagesPanel({ memberId, memberName, onOpenFull }: { memberId: number; memberName: string; onOpenFull: () => void }) {
  const [, navigate] = useLocation();
  const { canEdit } = usePermissions();
  const canSendMessages = canEdit("messages");
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const timelineQuery = useQuery<TimelineChat[]>({
    queryKey: ["/api/app/members", memberId, "timeline"],
  });
  const recent = (timelineQuery.data ?? []).slice(-20);
  return (
    <div className="mt-8 border border-gray-200 rounded-lg p-4" data-testid="panel-member-messages" data-tour="member-detail-history">
      <NewMessageModal
        open={newMessageOpen}
        onOpenChange={setNewMessageOpen}
        presetMemberId={memberId}
        onSent={(mid) => navigate(`/app/messages?memberId=${mid}&exact=1`)}
      />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-700">Recent Messages</h3>
        </div>
        <div className="flex items-center gap-2">
          {canSendMessages && (
            <Button
              size="sm"
              onClick={() => setNewMessageOpen(true)}
              data-testid="button-send-message"
            >
              <MessageSquare className="h-4 w-4 mr-1" />
              Send Message
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onOpenFull} data-testid="button-jump-messages">
            <MessageSquare className="h-4 w-4 mr-1" />
            Open thread
          </Button>
        </div>
      </div>
      {timelineQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-12 w-2/3 ml-auto" />
          <Skeleton className="h-12 w-3/4" />
        </div>
      ) : recent.length === 0 ? (
        <p className="text-xs text-gray-400" data-testid="empty-member-messages">
          No messages yet with {memberName}.
        </p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-tour="member-detail-messages">
          {recent.map((m) => {
            const isOutbound = m.senderType === "admin";
            const attachments = timelineAttachments(m);
            return (
              <div key={m.id} className={`flex ${isOutbound ? "justify-end" : "justify-start"}`} data-testid={`bubble-message-${m.id}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${isOutbound ? "bg-brand text-white" : "bg-gray-100 text-gray-900"}`}>
                  {m.messageText ? (
                    <MessageBody text={m.messageText} />
                  ) : null}
                  {attachments.length > 0 ? (
                    <MessageAttachments
                      urls={attachments}
                      messageId={m.id}
                      tone={isOutbound ? "dark" : "light"}
                    />
                  ) : null}
                  <div className={`mt-1 text-[10px] ${isOutbound ? "text-blue-100" : "text-gray-500"}`}>
                    {(m.senderName || (isOutbound ? "You" : memberName))} · {m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4" data-tour="member-detail-info">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        {icon}
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value, testId }: { icon: React.ReactNode; label: string; value: string; testId: string }) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-sm text-gray-500 w-28">{label}</span>
      <span className="text-sm text-gray-900" data-testid={testId}>{value}</span>
    </div>
  );
}

function HarvestRow({
  icon,
  label,
  testId,
  hasValue,
  placeholder,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  testId: string;
  hasValue: boolean;
  placeholder: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2" data-testid={`row-${testId.replace(/^text-detail-/, "harvest-")}`}>
      {icon}
      <span className="text-sm text-gray-500 w-28">{label}</span>
      {hasValue ? (
        <span data-testid={testId}>{children}</span>
      ) : (
        <span className="text-sm text-gray-400 italic" data-testid={testId}>
          {placeholder}
        </span>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs text-gray-500 mb-1">{label}</Label>
      {children}
    </div>
  );
}
// Task #1475 — Shows stay and move-in reviews for a member on their detail page.
type ReviewWithType = (PadsplitStayReview | PadsplitMoveInReview) & { reviewType: "stay" | "move_in" };

function MemberReviewsSection({ memberId, memberName }: { memberId: number; memberName: string }) {
  const stayQuery = useQuery<PadsplitStayReview[]>({
    queryKey: ["/api/app/reviews/stay", { memberId }],
    queryFn: async () => {
      const res = await fetch(`/api/app/reviews/stay?memberId=${memberId}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json() as Promise<PadsplitStayReview[]>;
    },
  });
  const moveInQuery = useQuery<PadsplitMoveInReview[]>({
    queryKey: ["/api/app/reviews/move-in", { memberId }],
    queryFn: async () => {
      const res = await fetch(`/api/app/reviews/move-in?memberId=${memberId}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json() as Promise<PadsplitMoveInReview[]>;
    },
  });

  const stayReviews = stayQuery.data ?? [];
  const moveInReviews = moveInQuery.data ?? [];
  const all: ReviewWithType[] = [
    ...stayReviews.map(r => ({ ...r, reviewType: "stay" as const })),
    ...moveInReviews.map(r => ({ ...r, reviewType: "move_in" as const })),
  ].sort((a, b) => {
    const da = a.reviewDate ? new Date(a.reviewDate).getTime() : 0;
    const db = b.reviewDate ? new Date(b.reviewDate).getTime() : 0;
    return db - da;
  });

  if (!stayQuery.isLoading && !moveInQuery.isLoading && all.length === 0) return null;

  const manageUrl = `/app/reviews?tab=manage&member=${encodeURIComponent(memberName)}`;

  return (
    <Section title="PadSplit Reviews" icon={<Star className="h-4 w-4 text-amber-500" />}>
      <div className="flex items-center justify-between mb-3 -mt-1">
        <span className="text-xs text-gray-500">
          {all.length > 0 ? `${all.length} review${all.length !== 1 ? "s" : ""} on file` : "Loading…"}
        </span>
        <div className="flex items-center gap-3">
          <a href={manageUrl} className="flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid="link-manage-member-reviews">
            Manage <ChevronRight className="h-3 w-3" />
          </a>
          <a href="/app/reviews" className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="link-all-member-reviews">
            View all <ChevronRight className="h-3 w-3" />
          </a>
        </div>
      </div>
      {(stayQuery.isLoading || moveInQuery.isLoading) ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : (
        <div className="space-y-2">
          {all.slice(0, 5).map((r) => {
            const isBad = (r.rating != null && r.rating < 3) || r.sentiment === "negative";
            return (
              <div
                key={`${r.reviewType}-${r.id}`}
                className={`rounded-lg border px-3 py-2 text-sm ${isBad ? "border-red-200 bg-red-50/30" : "border-gray-100 bg-gray-50"}`}
                data-testid={`card-member-review-${r.id}`}
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {r.rating != null && (
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`h-3 w-3 ${i <= (r.rating as number) ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
                      ))}
                    </div>
                  )}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full border font-medium ${r.reviewType === "stay" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-purple-50 text-purple-700 border-purple-200"}`}>
                    {r.reviewType === "stay" ? "Stay" : "Move-In"}
                  </span>
                  {r.reviewDate && <span className="text-xs text-gray-400">{new Date(r.reviewDate).toLocaleDateString()}</span>}
                  {isBad && <span className="text-xs text-red-600 font-medium">⚠ Low/Negative</span>}
                </div>
                {r.reviewText && (
                  <p className="text-xs text-gray-700 line-clamp-2">"{r.reviewText}"</p>
                )}
              </div>
            );
          })}
          {all.length > 5 && (
            <a href="/app/reviews" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              +{all.length - 5} more reviews <ChevronRight className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </Section>
  );
}

// Task #1029 — Reads from member_chats via the new timeline endpoint.
function MemberConversationSection({ memberId, memberName }: { memberId: number; memberName: string | null | undefined }) {
  const [, navigate] = useLocation();
  const timelineQuery = useQuery<TimelineChat[]>({
    queryKey: ["/api/app/members", memberId, "timeline"],
    enabled: !isNaN(memberId),
  });
  const timeline = timelineQuery.data ?? [];
  const recentMessages = timeline.slice(-8);

  return (
    <Section title="Messages" icon={<MessageSquare className="h-4 w-4" />}>
      <div className="flex items-center justify-between mb-3 -mt-1">
        <span className="text-xs text-gray-500" data-testid="text-messages-summary">
          {timeline.length > 0
            ? `Conversation with ${memberName || "member"}`
            : "No messages yet for this member"}
        </span>
        <Button
          variant="outline"
          size="sm"
          asChild
          data-testid="button-jump-messages"
        >
          <Link href={`/app/messages?memberId=${memberId}&exact=1`}>
            <ExternalLink className="h-3.5 w-3.5 mr-1" />
            Open in Messages
          </Link>
        </Button>
      </div>

      {timelineQuery.isLoading ? (
        <div className="space-y-2" data-testid="text-messages-loading">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      ) : recentMessages.length === 0 ? (
        <p className="text-sm text-gray-400 italic" data-testid="text-no-messages">
          No messages exchanged yet.
        </p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="list-recent-messages" data-tour="member-detail-messages">
          {recentMessages.map((m) => {
            const inbound = m.senderType === "member";
            const attachments = timelineAttachments(m);
            const hasBody = !!m.messageText;
            return (
              <div
                key={m.id}
                className={`flex ${inbound ? "justify-start" : "justify-end"}`}
                data-testid={`message-${m.id}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    inbound
                      ? "bg-gray-100 text-gray-900"
                      : "bg-brand text-white"
                  }`}
                >
                  {hasBody || attachments.length === 0 ? (
                    <MessageBody text={m.messageText || "—"} />
                  ) : null}
                  {attachments.length > 0 ? (
                    <MessageAttachments
                      urls={attachments}
                      messageId={m.id}
                      tone={inbound ? "light" : "dark"}
                    />
                  ) : null}
                  <div className={`text-[10px] mt-1 ${inbound ? "text-gray-500" : "text-blue-100"}`}>
                    {m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
